/**
 * Load texture images into MapLibre map for fill-pattern usage.
 * Browser-only utility - requires DOM Image API.
 *
 * Uses batched loading (5 at a time) with requestAnimationFrame pauses
 * to prevent WebGL context loss from parallel GPU resource allocation.
 */

/**
 * Load a single texture into the map
 */
function loadSingleTexture(
  map: any,
  texture: { slug: string },
  textureBase: string
): Promise<void> {
  return new Promise<void>((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        if (!map.hasImage(texture.slug)) {
          map.addImage(texture.slug, img);
        }
        resolve();
      } catch (err) {
        console.warn(`Failed to add texture ${texture.slug}:`, err);
        resolve(); // Don't fail the whole load
      }
    };
    img.onerror = () => {
      console.warn(`Failed to load texture image: ${texture.slug}`);
      resolve(); // Don't fail the whole load
    };
    img.src = `${textureBase}/${texture.slug}.png`;
  });
}

/**
 * Load all textures from TEXTURE_LIBRARY into the map.
 * Textures must be available at /sprites/textures/{slug}.png
 *
 * Uses batched loading (5 at a time) with requestAnimationFrame pauses
 * between batches to give the GPU breathing room and prevent context loss.
 *
 * @param map - MapLibre map instance
 * @param textureBase - Base URL for texture images (default: "/sprites/textures")
 * @returns Promise that resolves when all textures are loaded
 */
export async function loadTextures(
  map: any,
  textureBase: string = "/sprites/textures"
): Promise<void> {
  const { TEXTURE_LIBRARY } = await import("../textures");

  const BATCH_SIZE = 5;

  for (let i = 0; i < TEXTURE_LIBRARY.length; i += BATCH_SIZE) {
    const batch = TEXTURE_LIBRARY.slice(i, i + BATCH_SIZE);
    await Promise.all(batch.map((t) => loadSingleTexture(map, t, textureBase)));

    // Give GPU breathing room between batches
    if (i + BATCH_SIZE < TEXTURE_LIBRARY.length) {
      await new Promise((r) => requestAnimationFrame(r));
    }
  }
}
